import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Button, Container, Alert, Spinner } from 'react-bootstrap';
import api from '../services/api';

const ForgotPassword = () => {
  const [userInput, setUserInput] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [step, setStep] = useState(1);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  // 1. Add loading state
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const clearMessages = () => {
    setMessage('');
    setError('');
  };

  // 2. Update OTP request handler
  const handleRequestOtp = async (e) => {
    if (e) e.preventDefault();
    clearMessages();
    if (!userInput.trim()) {
      setError('Please enter your User ID, Username, or Email.');
      return;
    }
    setIsLoading(true);
    try {
      await api.post('users/forgot-password-request/', { user_id: userInput });
      setMessage('An OTP has been sent to your registered email address. Please check your inbox (and spam folder).');
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.error || 'Error requesting OTP. Please ensure your identifier is correct and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // 2. Update password reset handler
  const handleResetPassword = async (e) => {
    if (e) e.preventDefault();
    clearMessages();
    if (!otp.trim() || !newPassword.trim()) {
        setError('OTP and New Password are required.');
        return;
    }
    setIsLoading(true);
    try {
      await api.post('users/verify-otp-and-reset-password/', {
        user_id: userInput,
        otp: otp,
        new_password: newPassword,
      });
      setMessage('Password reset successful. You can now login.');
      setOtp('');
      setNewPassword('');
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Error resetting password. Please check the OTP or request a new one.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
      <div style={{ width: '100%', maxWidth: '400px' }}>
        <h2 className="text-center mb-4">Forgot Password</h2>
        {message && <Alert variant="success" onClose={() => setMessage('')} dismissible>{message}</Alert>}
        {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
        
        {step === 1 && (
          <Form onSubmit={handleRequestOtp}>
            <Form.Group controlId="userInput" className="mb-3">
              <Form.Label>User ID / Username / Email:</Form.Label>
              <Form.Control
                type="text"
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                placeholder="Enter your User ID, Username, or Email"
                required
                autoFocus
              />
            </Form.Group>
            {/* 3. Update Button with Spinner */}
            <Button variant="primary" type="submit" className="w-100" disabled={!userInput.trim() || isLoading}>
              {isLoading ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : 'Request OTP'}
            </Button>
          </Form>
        )}

        {step === 2 && (
          <Form onSubmit={handleResetPassword}>
             <Alert variant="info" className="mb-3">
               An OTP was sent to the email associated with: <strong>{userInput}</strong>.
             </Alert>
            <Form.Group controlId="otp" className="mb-3">
              <Form.Label>Enter OTP:</Form.Label>
              <Form.Control
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                placeholder="Enter the 6-digit OTP"
                required
                maxLength={6}
                autoFocus
              />
            </Form.Group>
            <Form.Group controlId="newPassword" className="mb-3">
              <Form.Label>New Password:</Form.Label>
              <Form.Control
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter your new password"
                required
              />
            </Form.Group>
            {/* 3. Update Buttons with Spinner and disabled state */}
            <Button variant="success" type="submit" className="w-100 mb-2" disabled={!otp.trim() || !newPassword.trim() || isLoading}>
              {isLoading ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : 'Reset Password'}
            </Button>
            <Button variant="outline-secondary" className="w-100" onClick={() => { setStep(1); clearMessages(); setOtp(''); setNewPassword(''); }} disabled={isLoading}>
              Request OTP for a different account?
            </Button>
          </Form>
        )}

        <Button variant="link" onClick={() => navigate('/login')} className="mt-3 d-block mx-auto" disabled={isLoading}>
          Back to Login
        </Button>
      </div>
    </Container>
  );
};

export default ForgotPassword;